import { useCallback } from 'react'

import { InfiniteData } from '@tanstack/react-query'

import {
  Chat,
  ChatList,
  QueryFindChatWithMemberArgs,
} from '@tribeplatform/gql-client/types'

import { UseQueryOptions, useQuery, useQueryClient } from '../../index.js'
import { useTribeClient } from '../../useTribeClient.js'
import {
  getChatsKey,
  getChatWithMemberKey,
} from '../../utils/keys/messaging.key.js'
import { ARCHIVED_CHATS_QUERY, CHATS_QUERY } from './constants.js'

export const useFindChatWithMember = (options: {
  variables: QueryFindChatWithMemberArgs
  useQueryOptions?: UseQueryOptions<Chat>
}) => {
  const { variables, useQueryOptions } = options

  const { client } = useTribeClient()
  const chatWithMemberKey = getChatWithMemberKey({ variables })
  const queryClient = useQueryClient()
  const chatsKey = getChatsKey({ variables: { ...CHATS_QUERY } })
  const archivedChatsKey = getChatsKey({
    variables: { ...ARCHIVED_CHATS_QUERY },
  })

  const findChatInChats = useCallback(
    (chats: InfiniteData<ChatList> | undefined, recipientId: string) => {
      return chats?.pages
        .flatMap(page => page.data)
        .find(chat => chat.otherParticipant?.memberId === recipientId)
    },
    [],
  )

  return useQuery<Chat>(
    chatWithMemberKey,
    () => {
      const chats = queryClient.getQueryData<InfiniteData<ChatList>>(chatsKey)
      const archivedChats =
        queryClient.getQueryData<InfiniteData<ChatList>>(archivedChatsKey)

      const chat = findChatInChats(chats, variables.recipientId)
      if (chat) {
        return chat
      }

      const archivedChat = findChatInChats(archivedChats, variables.recipientId)
      if (archivedChat) {
        return archivedChat
      }

      return client.messaging.findChatWithMember(variables)
    },
    {
      ...useQueryOptions,
    },
  )
}
